import { Building2, Camera, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/utils/cn';

import { ImageCropper } from './image-cropper';

/**
 * Square crop upload for a church logo — rounded rectangle preview, not a circular
 * avatar or passport photo.
 *
 * `stacked` is for settings sidebars where the logo is the hero of the column.
 * `inline` is for forms that already have a long field list (sermon cover, etc.).
 */

export interface ChurchLogoUploaderProps {
  src?: string | null;
  name: string;
  onUpload: (blob: Blob) => Promise<void> | void;
  onRemove?: () => void;
  disabled?: boolean;
  /** `stacked` fills a narrow column; `inline` keeps a compact preview beside the actions. */
  layout?: 'inline' | 'stacked';
  labels: {
    change: string;
    remove: string;
    cropTitle: string;
    zoom: string;
    apply: string;
    cancel: string;
    instructions: string;
    closeCropper?: string;
  };
  className?: string;
}

export const ChurchLogoUploader = ({
  src,
  name,
  onUpload,
  onRemove,
  disabled = false,
  layout = 'inline',
  labels,
  className,
}: ChurchLogoUploaderProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [candidate, setCandidate] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const stacked = layout === 'stacked';

  useEffect(
    () => () => {
      if (candidate) URL.revokeObjectURL(candidate);
    },
    [candidate],
  );

  const openPicker = () => {
    if (!disabled) inputRef.current?.click();
  };

  const previewClassName = cn(
    'overflow-hidden rounded-lg border border-border bg-surface-sunken',
    stacked
      ? 'group relative aspect-square w-full cursor-pointer transition-colors hover:border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
      : 'flex size-20 shrink-0 items-center justify-center',
    stacked && !src && 'border-dashed',
  );

  const stackedUploadHint = stacked ? (
    <span
      aria-hidden
      className={cn(
        'absolute inset-0 flex flex-col items-center justify-center gap-1.5 px-3 text-center transition-opacity',
        src
          ? 'bg-overlay/0 text-fg-inverse opacity-0 group-hover:bg-overlay/55 group-hover:opacity-100 group-focus-visible:bg-overlay/55 group-focus-visible:opacity-100'
          : 'text-fg-secondary',
      )}
    >
      <Camera className={cn(src ? 'size-7' : 'size-10')} />
      <span className="text-caption font-medium">{labels.change}</span>
    </span>
  ) : null;

  const previewContent = src ? (
    <img
      src={src}
      alt={stacked ? '' : name}
      aria-hidden={stacked || undefined}
      className="size-full object-contain"
    />
  ) : (
    <div className="flex size-full items-center justify-center">
      {!stacked ? (
        <Building2 className="size-8 text-fg-muted" aria-hidden />
      ) : null}
    </div>
  );

  return (
    <div
      className={cn(
        stacked
          ? 'flex w-full max-w-64 flex-col items-stretch gap-3'
          : 'flex items-start gap-4',
        className,
      )}
    >
      {stacked ? (
        <button
          type="button"
          disabled={disabled}
          onClick={openPicker}
          aria-label={labels.change}
          className={previewClassName}
        >
          {previewContent}
          {stackedUploadHint}
        </button>
      ) : (
        <div className={previewClassName} aria-hidden={!src}>
          {previewContent}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {!stacked ? (
          <Button
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={openPicker}
            leadingIcon={<Camera className="h-4 w-4" />}
          >
            {labels.change}
          </Button>
        ) : null}

        {src && onRemove ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={onRemove}
            leadingIcon={<Trash2 className="h-4 w-4" />}
            className={cn('text-danger-fg', stacked && 'w-full justify-center')}
          >
            {labels.remove}
          </Button>
        ) : null}

        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) setCandidate(URL.createObjectURL(file));
            event.target.value = '';
          }}
        />
      </div>

      <Dialog open={candidate !== null} onOpenChange={(open) => !open && setCandidate(null)}>
        <DialogContent size="md" closeLabel={labels.closeCropper}>
          <DialogHeader>
            <DialogTitle>{labels.cropTitle}</DialogTitle>
          </DialogHeader>

          {candidate ? (
            <ImageCropper
              src={candidate}
              aspect={1}
              circular={false}
              maxSize={512}
              outputType="image/png"
              applying={uploading}
              onCancel={() => setCandidate(null)}
              onCropped={async (blob) => {
                setUploading(true);
                try {
                  await onUpload(blob);
                  setCandidate(null);
                } finally {
                  setUploading(false);
                }
              }}
              labels={{
                zoom: labels.zoom,
                apply: labels.apply,
                cancel: labels.cancel,
                instructions: labels.instructions,
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};
